import { addToCart } from '@nova/utils'

class RequiredWarrantyProduct extends HTMLElement {
  constructor() {
    super()

    this.optionButtons = this.querySelectorAll('.js-warranty-product-option-value')
    this.optionSelects = this.querySelectorAll('.js-warranty-product-option-value-select')
    this.addToCartButton = this.querySelector('.js-warranty-product-add-to-cart')
    this.priceCurrentEl = this.querySelector('.js-warranty-product-price-current')
    this.priceCompareEl = this.querySelector('.js-warranty-product-price-compare')
    this.statusEl = this.querySelector('[data-warranty-status]')
    this.variants = this._parseVariants()
    this.selectedOptions = this._getInitialSelectedOptions()
    this._isAdding = false
    this._isAdded = false
    this._addedTimeout = null
  }

  connectedCallback() {
    this.optionButtons.forEach((button) => {
      button.addEventListener('click', (event) => {
        event.preventDefault()
        if (button.hasAttribute('disabled') || this._isAdding) return

        this._clearAddedState()

        const optionIndex = Number(button.dataset.optionIndex)
        this.selectedOptions[optionIndex] = button.dataset.optionValue
        this._syncButtonsForOption(optionIndex, button.dataset.optionValue)
        this._syncSelectForOption(optionIndex, button.dataset.optionValue)
        this._applySelectedVariant()
      })
    })

    this.optionSelects.forEach((select) => {
      select.addEventListener('change', (event) => {
        event.preventDefault()
        if (this._isAdding) return

        this._clearAddedState()

        const optionIndex = Number(select.dataset.optionIndex)
        this.selectedOptions[optionIndex] = event.target.value
        this._syncButtonsForOption(optionIndex, event.target.value)
        this._applySelectedVariant()
      })
    })

    if (this.addToCartButton) {
      this.addToCartButton.addEventListener('click', (event) => {
        event.preventDefault()
        this.addToCart(this.addToCartButton.dataset.itemId)
      })
    }

    this._applySelectedVariant()
  }

  disconnectedCallback() {
    clearTimeout(this._addedTimeout)
  }

  _parseVariants() {
    const variantsScript = this.querySelector('.js-warranty-product-variants')
    if (!variantsScript) return []

    try {
      return JSON.parse(variantsScript.textContent.trim())
    } catch {
      console.error('Error parsing warranty product variants')
      return []
    }
  }

  _getInitialSelectedOptions() {
    if (!this.variants.length) return []

    const optionCount = this.querySelectorAll('.required-warranty-product-option').length
    const selected = new Array(optionCount).fill('')

    this.optionSelects.forEach((select) => {
      const optionIndex = Number(select.dataset.optionIndex)
      selected[optionIndex] = select.value
    })

    this.optionButtons.forEach((button) => {
      if (!button.classList.contains('active')) return
      const optionIndex = Number(button.dataset.optionIndex)
      selected[optionIndex] = button.dataset.optionValue
    })

    return selected
  }

  _findSelectedVariant() {
    if (!this.variants.length) return null

    if (this.variants.length === 1 && !this.querySelector('.required-warranty-product-option')) {
      return this.variants[0]
    }

    return (
      this.variants.find((variant) =>
        variant.options.every((optionValue, index) => optionValue === this.selectedOptions[index]),
      ) || null
    )
  }

  _getModalRoot() {
    return this.closest('modal-required-warranty-root')
  }

  _moveFocusFromAddButton() {
    const modal = this._getModalRoot()
    if (typeof modal?.moveFocusFromElement === 'function') {
      modal.moveFocusFromElement(this.addToCartButton)
      return
    }

    if (
      this.addToCartButton &&
      (document.activeElement === this.addToCartButton ||
        this.addToCartButton.contains(document.activeElement))
    ) {
      this.addToCartButton.blur()
    }
  }

  _setAddButtonLabel(isAdded) {
    if (!this.addToCartButton) return

    const label = isAdded
      ? this.addToCartButton.dataset.addedLabel
      : this.addToCartButton.dataset.addLabel

    if (label) {
      this.addToCartButton.setAttribute('aria-label', label)
    }
  }

  _announceStatus(message) {
    if (!this.statusEl || !message) return

    this.statusEl.textContent = ''
    // Clear first so repeated adds re-trigger the live region.
    requestAnimationFrame(() => {
      this.statusEl.textContent = message
    })
  }

  _applySelectedVariant() {
    if (!this.addToCartButton) return

    const variant = this._findSelectedVariant()

    if (this.priceCurrentEl && variant) {
      this.priceCurrentEl.textContent = variant.price_formatted
      this.priceCurrentEl.classList.toggle('c-danger', Boolean(variant.compare_at_price))
      this.priceCurrentEl.classList.toggle('c-grey-600', !variant.compare_at_price)
    }

    if (this.priceCompareEl) {
      if (variant?.compare_at_price && variant.compare_at_price_formatted) {
        this.priceCompareEl.textContent = variant.compare_at_price_formatted
        this.priceCompareEl.classList.remove('hidden')
      } else {
        this.priceCompareEl.textContent = ''
        this.priceCompareEl.classList.add('hidden')
      }
    }

    // Keep the button locked while an add is in flight or "Added" feedback is showing.
    if (this._isAdding || this._isAdded) {
      if (variant) this.addToCartButton.setAttribute('data-item-id', variant.id)
      else this.addToCartButton.removeAttribute('data-item-id')
      this.addToCartButton.setAttribute('disabled', true)
      return
    }

    if (!variant) {
      this.addToCartButton.removeAttribute('data-item-id')
      this.addToCartButton.setAttribute('disabled', true)
      this._setAddButtonLabel(false)
      return
    }

    this.addToCartButton.setAttribute('data-item-id', variant.id)
    this.addToCartButton.toggleAttribute('disabled', !variant.available)
    this._setAddButtonLabel(false)
  }

  _syncSelectForOption(optionIndex, optionValue) {
    const select = this.querySelector(
      `.js-warranty-product-option-value-select[data-option-index="${optionIndex}"]`,
    )
    if (select) select.value = optionValue
  }

  _syncButtonsForOption(optionIndex, optionValue) {
    const optionGroup = this.querySelector(
      `.required-warranty-product-option[data-option-index="${optionIndex}"]`,
    )
    if (!optionGroup) return

    optionGroup.querySelectorAll('.js-warranty-product-option-value').forEach((button) => {
      const isSelected = button.dataset.optionValue === optionValue
      button.classList.toggle('active', isSelected)
      button.setAttribute('aria-pressed', isSelected ? 'true' : 'false')
    })
  }

  _clearAddedState() {
    clearTimeout(this._addedTimeout)
    this._addedTimeout = null
    this._isAdded = false
    this.addToCartButton?.classList.remove('is-added')
    this._setAddButtonLabel(false)
  }

  _showAddedState() {
    this._clearAddedState()
    this._isAdded = true
    this.addToCartButton?.classList.add('is-added')
    this.addToCartButton?.setAttribute('disabled', true)
    this._setAddButtonLabel(true)
    this._announceStatus(this.addToCartButton?.dataset.addedLabel || '')
    this._moveFocusFromAddButton()

    this._addedTimeout = setTimeout(() => {
      this._addedTimeout = null
      this._isAdded = false
      this.addToCartButton?.classList.remove('is-added')
      this._setAddButtonLabel(false)
      this._applySelectedVariant()
    }, 3000)
  }

  async addToCart(itemId) {
    if (!itemId || this._isAdding || this._isAdded) return

    this._isAdding = true
    this.addToCartButton?.setAttribute('disabled', true)
    this._moveFocusFromAddButton()

    try {
      await addToCart([{ id: itemId, quantity: 1 }], false)
    } catch (error) {
      console.error('Error adding warranty product to cart', error)
      this._isAdding = false
      this._applySelectedVariant()
      return
    }

    try {
      await this.reloadMiniCart()
    } catch (error) {
      console.error('Error reloading mini cart after warranty add', error)
    }

    this._isAdding = false
    this._showAddedState()
  }

  async reloadMiniCart() {
    await new Promise((resolve, reject) => {
      document.dispatchEvent(
        new CustomEvent('nova:cart:reload', {
          bubbles: true,
          composed: true,
          detail: { open: false, resolve, reject },
        }),
      )
    })
  }
}

if (!customElements.get('required-warranty-product')) {
  customElements.define('required-warranty-product', RequiredWarrantyProduct)
}
